import { useEffect, useRef, useState } from 'react';
import type { PipelineRunEventFrame } from '@/types/api';

const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? '';

export type EventStreamStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed'
  | 'error';

export function useEventStream(
  runId: string | null,
  enabled: boolean,
  onFrame: (frame: PipelineRunEventFrame) => void
): {
  status: EventStreamStatus;
  latest: PipelineRunEventFrame | null;
  cursor: string | null;
  reconnect: () => void;
} {
  const [status, setStatus] = useState<EventStreamStatus>('idle');
  const [latest, setLatest] = useState<PipelineRunEventFrame | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [reconnectTick, setReconnectTick] = useState(0);
  const handlerRef = useRef(onFrame);
  const cursorRef = useRef<string | null>(null);
  handlerRef.current = onFrame;
  cursorRef.current = cursor;

  useEffect(() => {
    if (!runId || !enabled) {
      setStatus('idle');
      return;
    }
    if (typeof EventSource === 'undefined') {
      setStatus('error');
      return;
    }
    setStatus('connecting');
    const since = cursorRef.current;
    const token = localStorage.getItem('jwt');
    const qs = new URLSearchParams();
    if (since) qs.set('since', since);
    if (token) qs.set('access_token', token);
    const query = qs.toString();
    const url =
      `${API_BASE_URL}/api/v1/pipelines/runs/${encodeURIComponent(runId)}/events` +
      (query ? `?${query}` : '');
    const es = new EventSource(url, { withCredentials: true });
    let backoff = 1_000;
    let retryHandle: ReturnType<typeof setTimeout> | null = null;

    es.onopen = () => {
      setStatus('open');
      backoff = 1_000;
    };
    es.onmessage = (ev) => {
      try {
        const frame = JSON.parse(ev.data) as PipelineRunEventFrame;
        const frame_cursor = 'cursor' in frame ? frame.cursor : undefined;
        if (frame_cursor) {
          setCursor(frame_cursor);
          cursorRef.current = frame_cursor;
        }
        setLatest(frame);
        handlerRef.current(frame);
      } catch {
      }
    };
    es.onerror = () => {
      setStatus('reconnecting');
      es.close();
      retryHandle = setTimeout(() => {
        backoff = Math.min(backoff * 2, 8_000);
        setReconnectTick((t) => t + 1);
      }, backoff);
    };

    return () => {
      if (retryHandle) clearTimeout(retryHandle);
      es.close();
      setStatus('closed');
    };
  }, [runId, enabled, reconnectTick]);

  const reconnect = (): void => {
    setReconnectTick((t) => t + 1);
  };

  return { status, latest, cursor, reconnect };
}
